import { useLocation, useParams } from "wouter"
import "./individualProject.css"
import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import { Table } from "../../../components/table/Table"
import { getProject } from "../../../fetch/projects"
import { Project, UserRole } from "../../../../types"
import { ProjectHeading } from "./ProjectHeading"
import { BreadCrumbs } from "../../../components/breadcrumbs/BreadCrumbs"
import { Status } from "../../../components/status/Status"
import { isBitus } from "../../../../config/urls"
import { useWatcher } from "../../../lib/state/useWatcher"
import { tenantState } from "../../../store/tenant"
import { EditOperation } from "./EditOperation"
import { AddNewProject } from "../projects/AddNewProject"
import { ExportProject } from "./ExportProject"
import { Portal } from "@/components/portal/Portal"
import { hasUserAccess } from "@/utils/user"
import { userState } from "@/store/user"

const columns = [
  { title: "Status", id: "status" },
  { title: "Normatīvi", id: "difficulty" },
  { title: isBitus ? "Iecirknis" : "Projekts", id: "name" },
  { title: "Paredzētais laiks", id: "timePredicted" },
  { title: "Patērētais laiks", id: "timeUsed" },
  { title: "Prognozētās", id: "unitsPredicted" },
  { title: "Zaudējumi", id: "salaryDiff" },
  { title: "Vienības", id: "unitsUsed" },
  { title: "Summa", id: "salary" },
  { title: "", id: "edit" },
]

type Row = ComponentProps<typeof Table>["rows"]

export const IndividualProject = () => {
  const [, setLocation] = useLocation()
  const {
    settings: { features },
  } = useWatcher(tenantState)
  const { id } = useParams()

  const [project, setProject] = useState<Project | undefined>()
  const [tableData, setTableData] = useState<
    ComponentProps<typeof Table>["rows"]
  >([])
  const user = useWatcher(userState)
  const canExport =
    project?.id != null &&
    hasUserAccess({
      neededRole: UserRole.Admin,
      userRole: user.role,
    })
  const init = useCallback(() => {
    if (!id) {
      return
    }
    getProject(id).then(async (res) => {
      setProject(res.project)
      setTableData(prepareTableData(res.items))
    })
  }, [id])

  useEffect(() => {
    init()
  }, [id, init])

  const prepareTableData = (data: Record<string, any>[]) => {
    const rows: ComponentProps<typeof Table>["rows"] = []
    data.forEach((row) => {
      const curr: Row[number] = { id: row.id }
      const progress = features["units"] ? row.progress : row.timeProgress
      columns.forEach(({ id }) => {
        const value = row[id]
        switch (id) {
          case "status": {
            curr[id] = (
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Status status={row.status} />
                <span style={{ fontSize: 20, fontWeight: 500 }}>
                  {progress}%
                </span>
              </div>
            )
            break
          }
          case "difficulty": {
            curr[id] = (
              <span
                style={{
                  fontSize: 20,
                  fontWeight: 500,
                  color: row.difficulty > 100 ? "var(--error)" : undefined,
                }}
              >
                {row.difficulty}%
              </span>
            )
            break
          }
          case "name": {
            curr[id] = value
            break
          }
          case "timePredicted":
          case "timeUsed": {
            curr[id] = value
              ? `${Math.floor(value / 60)}h ${value % 60}min`
              : "-"
            break
          }
          case "salaryDiff": {
            const val = value ? `${value / 100}€` : "-"
            if (value < 0) {
              curr.error = true
            }
            curr[id] = (
              <span style={{ color: value < 0 ? "var(--error)" : "" }}>
                {val}
              </span>
            )
            break
          }
          case "salary": {
            const v = row["salaryUsed"]
            curr[id] = v ? `${v / 100}€` : "-"
            break
          }
          case "unitsPredicted":
          case "unitsUsed": {
            curr[id] = value
            break
          }
          case "edit": {
            curr[id] = <EditOperation project={row as Project} onSave={init} />
          }
        }
      })
      rows.push(curr)
    })
    return rows
  }

  const filteredColumns = useMemo(() => {
    return columns.filter((col) => {
      if (col.id === "timeUsed") {
        return features["time"]
      }

      if (col.id === "unitsUsed") {
        return features["units"]
      }

      if (col.id === "salary" || col.id === "salaryDiff") {
        return features["salary"]
      }

      if (col.id === "difficulty") {
        return features["normatives"]
      }

      return true
    })
  }, [features])

  return (
    <div>
      <AddNewProject onAdd={init} id={project?.id} />
      {canExport && (
        <Portal id="tabs-right">
          <ExportProject projectId={project.id} />
        </Portal>
      )}
      <BreadCrumbs
        items={[
          { path: "/", title: "Projekti" },
          { path: `/../${project?.id}`, title: project?.name ?? "" },
        ]}
      />
      {project && (
        <ProjectHeading
          project={project}
          features={features}
          showEdit
          onSave={init}
        />
      )}
      <Table
        columns={filteredColumns}
        rows={tableData}
        onRowClick={(id) => setLocation(`/${project?.id}/${id}`)}
      />
    </div>
  )
}
